import { useState, useEffect, useRef, useCallback } from 'react';
import { isCentAmount, roundFloatToCents } from '@hydra/internal';
import { floatVal, Nullable } from '@jamesgmarks/utilities';
import { useAppSelector } from '../../../../redux/hooks';
import { makeYmd, toDollarAmount } from '../../../../app-utils';
import { Invoice } from '../../../../../../entities/hydra';
import { REACT_APP_API_ROOT_URI } from '../../../../App';
import { typedApiFetch } from '../../../../redux/utils';
import { TCreditNoteWithBillingContacts } from '../../../../types/TCreditNoteWithBillingContacts';
import { ICreditNoteApplications, IInvoiceApplication, IFlattenedApplication } from './types';

// Constants
const FORM_RESET_DELAY = 1500;

const getInvoiceNumberFromApplication = (invoice: Nullable<Invoice>): Nullable<string> => (
  invoice?.invoiceNumber ?? null
);

/**
 * Returns the numeric outstanding amount for an invoice, parsed from its
 * `freshbooksOutstandingAmount` string field. Returns `null` when the field is
 * absent so callers can distinguish "zero outstanding" from "not yet loaded".
 */
const getOutstandingAmountFromInvoice = (invoice: Nullable<Invoice>): Nullable<number> => (
  invoice?.freshbooksOutstandingAmount != null
    ? floatVal(invoice.freshbooksOutstandingAmount)
    : null
);

export const useCreditNoteApplications = (
  selectedCreditNotes: TCreditNoteWithBillingContacts[],
  open: boolean,
  onClose: () => void,
  onSuccess?: () => void,
) => {
  const [creditNoteApplications, setCreditNoteApplications] = useState<ICreditNoteApplications[]>([]);
  const [applicationDate, setApplicationDate] = useState<Date>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Get invoices from redux store
  const invoices = useAppSelector(state => state.dynamicHydra.data.invoices?.list ?? []);

  // Invoice selection modal state
  const [invoiceSelectionOpen, setInvoiceSelectionOpen] = useState(false);
  const [selectedCreditNoteForInvoice, setSelectedCreditNoteForInvoice] = useState<Nullable<number>>(null);

  // Helper function to create initial invoice applications from credit note
  const createInitialApplicationsFromCreditNote = useCallback((
    creditNote: TCreditNoteWithBillingContacts,
  ): IInvoiceApplication[] => {
    const credits = creditNote.credits ?? [];
    const invoiceIds = Array.from(new Set(
      credits
        .map(credit => credit.invoiceId),
    ));

    return invoiceIds.map(invoiceId => {
      const sourceCredit = credits.find(credit => credit.invoiceId === invoiceId);
      const sourceInvoice = sourceCredit?.invoice ?? null;
      const invoiceSpecificAmount = credits
        .filter(credit => credit.invoiceId === invoiceId)
        .reduce((total, credit) => (
          total + floatVal(credit.amount ?? 0) + floatVal(credit.taxAmount ?? 0)
        ), 0);

      return {
        invoiceId,
        invoiceNumber: getInvoiceNumberFromApplication(sourceInvoice),
        invoice: sourceInvoice,
        amount: invoiceSpecificAmount,
      };
    });
  }, []);

  // Helper function to get total amount from credit note
  const getCreditNoteTotal = useCallback((creditNote: TCreditNoteWithBillingContacts): number => {
    const subtotal = floatVal(creditNote.subtotalAmount);
    const taxAmount = floatVal(creditNote.taxAmount);

    if (subtotal + taxAmount > 0) {
      return subtotal + taxAmount;
    }

    const credits = creditNote.credits ?? [];

    return credits.reduce(
      (total, credit) => total + floatVal(credit.amount) + floatVal(credit.taxAmount),
      0,
    );
  }, []);

  /**
   * Calculates the credit amount still available to apply for the given credit note.
   *
   * The value is derived by subtracting the sum of all non-deleted, non-void
   * `creditsApplied` amounts from the credit note total. The result is clamped to
   * a minimum of `0` so the UI never shows a negative available balance.
   *
   * This replaces the previous `getCreditNoteTotal` export used by the parent modal:
   * unlike that function, this one accounts for credit that has already been applied.
   */
  const getAvailableCreditAmount = useCallback((creditNote: TCreditNoteWithBillingContacts): number => {
    const appliedCreditAmount = (creditNote.creditsApplied ?? [])
      .filter(creditApplied => creditApplied.hydraState === 'active')
      .reduce((total, creditApplied) => total + floatVal(creditApplied.amount), 0);

    return Math.max(0, getCreditNoteTotal(creditNote) - appliedCreditAmount);
  }, [getCreditNoteTotal]);

  // Initialize applications when modal opens
  useEffect(() => {
    if (!open || selectedCreditNotes.length === 0) {
      return;
    }

    // Reset the updated invoice IDs tracker
    updatedInvoiceIdsRef.current = new Set();

    const initialApplications: ICreditNoteApplications[] = selectedCreditNotes.map(creditNote => ({
      creditNoteId: creditNote.id,
      creditNote,
      invoiceApplications: createInitialApplicationsFromCreditNote(creditNote),
      isExpanded: true,
    }));

    setCreditNoteApplications(initialApplications);
    setError(null);
    setSuccess(null);
  }, [open, selectedCreditNotes, createInitialApplicationsFromCreditNote]);

  // Update invoice details when invoices are loaded
  const updatedInvoiceIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (creditNoteApplications.length === 0) {
      return;
    }

    // Check if there are any new invoices that haven't been updated yet
    const newInvoices = invoices.filter(invoice =>
      !updatedInvoiceIdsRef.current.has(invoice.id));

    if (newInvoices.length === 0) {
      return;
    }

    setCreditNoteApplications(prev =>
      prev.map(creditNoteApp => ({
        ...creditNoteApp,
        invoiceApplications: creditNoteApp.invoiceApplications.map(invApp => {
          // Only update if we don't have the invoice details yet
          if (invApp.invoice || !invApp.invoiceId) {
            return invApp;
          }

          const foundInvoice = newInvoices.find(inv => inv.id === invApp.invoiceId);
          if (foundInvoice) {
            updatedInvoiceIdsRef.current.add(foundInvoice.id);
            return {
              ...invApp,
              invoice: foundInvoice,
              invoiceNumber: getInvoiceNumberFromApplication(foundInvoice),
            };
          }
          return invApp;
        }),
      })));
  }, [invoices, creditNoteApplications.length]);

  // Remove all invoices from all credit notes
  const removeAllInvoices = useCallback(() => {
    setCreditNoteApplications(prev =>
      prev.map(creditNoteApp => ({
        ...creditNoteApp,
        invoiceApplications: [],
      })));
  }, []);

  // Add all source invoices back (re-populate from credits)
  const addAllSourceInvoices = useCallback(() => {
    const refreshedApplications: ICreditNoteApplications[] = selectedCreditNotes.map(creditNote => {
      const invoiceApplications = createInitialApplicationsFromCreditNote(creditNote)
        .map(invApp => {
          const invoice = invoices.find(inv => inv.id === invApp.invoiceId) ?? null;
          return {
            ...invApp,
            invoice,
            invoiceNumber: getInvoiceNumberFromApplication(invoice) ?? invApp.invoiceNumber,
          };
        });

      return {
        creditNoteId: creditNote.id,
        creditNote,
        invoiceApplications,
        isExpanded: true,
      };
    });

    setCreditNoteApplications(refreshedApplications);
  }, [selectedCreditNotes, createInitialApplicationsFromCreditNote, invoices]);

  // Update application amount for a specific invoice within a credit note
  const updateInvoiceApplicationAmount = useCallback((creditNoteId: number, invoiceIndex: number, amount: number) => {
    setCreditNoteApplications(prev =>
      prev.map(creditNoteApp =>
        creditNoteApp.creditNoteId === creditNoteId
          ? {
            ...creditNoteApp,
            invoiceApplications: creditNoteApp.invoiceApplications.map((invApp, index) =>
              index === invoiceIndex ? { ...invApp, amount } : invApp),
          }
          : creditNoteApp));
  }, []);

  // Add a new invoice application to a credit note
  const addInvoiceApplication = useCallback((creditNoteId: number, invoice: Invoice) => {
    setCreditNoteApplications(prev =>
      prev.map(creditNoteApp =>
        creditNoteApp.creditNoteId === creditNoteId
          ? {
            ...creditNoteApp,
            invoiceApplications: [
              ...creditNoteApp.invoiceApplications,
              {
                invoiceId: invoice.id,
                invoiceNumber: getInvoiceNumberFromApplication(invoice),
                invoice,
                amount: 0,
              },
            ],
          }
          : creditNoteApp));
  }, []);

  // Remove an invoice application from a credit note
  const removeInvoiceApplication = useCallback((creditNoteId: number, invoiceIndex: number) => {
    setCreditNoteApplications(prev =>
      prev.map(creditNoteApp =>
        creditNoteApp.creditNoteId === creditNoteId
          ? {
            ...creditNoteApp,
            invoiceApplications: creditNoteApp.invoiceApplications.filter((_, index) => index !== invoiceIndex),
          }
          : creditNoteApp));
  }, []);

  // Toggle credit note card expansion
  const toggleCreditNoteExpansion = useCallback((creditNoteId: number) => {
    setCreditNoteApplications(prev =>
      prev.map(creditNoteApp =>
        creditNoteApp.creditNoteId === creditNoteId
          ? { ...creditNoteApp, isExpanded: !creditNoteApp.isExpanded }
          : creditNoteApp));
  }, []);

  // Open invoice selection modal
  const openInvoiceSelection = useCallback((creditNoteId: number) => {
    setSelectedCreditNoteForInvoice(creditNoteId);
    setInvoiceSelectionOpen(true);
  }, []);

  // Close invoice selection modal
  const closeInvoiceSelection = useCallback(() => {
    setInvoiceSelectionOpen(false);
    setSelectedCreditNoteForInvoice(null);
  }, []);

  // Handle multiple invoice selection from modal
  const handleInvoicesSelected = useCallback((invoices: Invoice[]) => {
    if (selectedCreditNoteForInvoice != null) {
      invoices.forEach(invoice => {
        addInvoiceApplication(selectedCreditNoteForInvoice, invoice);
      });
    }
    closeInvoiceSelection();
  }, [selectedCreditNoteForInvoice, addInvoiceApplication, closeInvoiceSelection]);

  // Validate all application data before submission
  const getSubmitValidationError = useCallback((): Nullable<string> => {
    // Flatten all invoice applications into a single list
    const allApplications: IFlattenedApplication[] = creditNoteApplications.flatMap(creditNoteApp =>
      creditNoteApp.invoiceApplications
        .filter((invApp): invApp is IInvoiceApplication & { invoiceId: number; invoice: Invoice } =>
          invApp.amount > 0 && invApp.invoiceId != null)
        .map(invApp => ({
          creditNoteId: creditNoteApp.creditNoteId,
          creditNote: creditNoteApp.creditNote,
          invoiceId: invApp.invoiceId,
          invoice: invApp.invoice,
          amount: invApp.amount,
        })));

    // Check if there are any applications
    if (allApplications.length === 0) {
      return 'Please specify amounts greater than 0 and select invoices for at least one credit note.';
    }

    // Check for invalid precision (2 decimal places)
    const invalidPrecisionApplication = allApplications.find(
      application => !isCentAmount(application.amount),
    );
    if (invalidPrecisionApplication) {
      const invoiceNum = invalidPrecisionApplication.invoice.invoiceNumber ?? 'N/A';
      const amount = invalidPrecisionApplication.amount;
      return `Invoice #${invoiceNum} has amount ${amount}. Credit applications must use 2 decimal places.`;
    }

    // Check for over-applied credit notes
    const overAppliedCreditNote = creditNoteApplications.find((creditNoteApp) => {
      const availableCreditAmount = getAvailableCreditAmount(creditNoteApp.creditNote);
      const totalApplyingAmount = creditNoteApp.invoiceApplications.reduce(
        (total, invoiceApplication) => total + (invoiceApplication.amount > 0 ? invoiceApplication.amount : 0),
        0,
      );
      // Compare cent-rounded amounts: summing many invoice-application floats can leave a
      // sub-cent residue that would otherwise falsely block submission as "over applied".
      return roundFloatToCents(totalApplyingAmount) > roundFloatToCents(availableCreditAmount);
    });
    if (overAppliedCreditNote) {
      const availableCreditAmount = getAvailableCreditAmount(overAppliedCreditNote.creditNote);
      const totalApplyingAmount = overAppliedCreditNote.invoiceApplications.reduce(
        (total, invoiceApplication) => total + (invoiceApplication.amount > 0 ? invoiceApplication.amount : 0),
        0,
      );
      const noteNum = overAppliedCreditNote.creditNote.noteNumber ?? overAppliedCreditNote.creditNote.id;
      const availableFormatted = toDollarAmount(availableCreditAmount);
      const applyingFormatted = toDollarAmount(totalApplyingAmount);
      return `Credit Note #${noteNum} has ${availableFormatted} available, but ${applyingFormatted} is being applied.`;
    }

    // Check for over-applied invoices
    const applicationTotalsByInvoice = creditNoteApplications.flatMap(
      creditNoteApp => creditNoteApp.invoiceApplications,
    ).reduce<Record<number, {
      amount: number;
      invoiceNumber: Nullable<string>;
      outstandingAmount: Nullable<number>;
    }>>((acc, invApp) => {
      if (invApp.invoiceId == null || invApp.amount <= 0) {
        return acc;
      }
      const previous = acc[invApp.invoiceId];
      const outstandingAmount = getOutstandingAmountFromInvoice(invApp.invoice);
      return {
        ...acc,
        [invApp.invoiceId]: {
          amount: (previous?.amount ?? 0) + invApp.amount,
          invoiceNumber: previous?.invoiceNumber ?? invApp.invoice?.invoiceNumber ?? invApp.invoiceNumber,
          outstandingAmount: previous?.outstandingAmount ?? outstandingAmount,
        },
      };
    }, {});

    const overAppliedInvoice = Object.values(applicationTotalsByInvoice)
      .find(({ amount, outstandingAmount }) => (
        outstandingAmount != null && roundFloatToCents(amount) > roundFloatToCents(outstandingAmount)
      ));
    if (overAppliedInvoice) {
      const invoiceNum = overAppliedInvoice.invoiceNumber ?? 'N/A';
      const outstandingFormatted = toDollarAmount(overAppliedInvoice.outstandingAmount ?? 0);
      return `Invoice #${invoiceNum} only has ${outstandingFormatted} outstanding. `
        + `Reduce applications to ${outstandingFormatted} or less.`;
    }

    return null;
  }, [creditNoteApplications, getAvailableCreditAmount]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate before submission
      const validationError = getSubmitValidationError();
      if (validationError) {
        setError(validationError);
        setIsSubmitting(false);
        return;
      }

      // Flatten all invoice applications into a single list (already validated above)
      const allApplications: IFlattenedApplication[] = creditNoteApplications.flatMap(creditNoteApp =>
        creditNoteApp.invoiceApplications
          .filter((invApp): invApp is IInvoiceApplication & { invoiceId: number; invoice: Invoice } =>
            invApp.amount > 0 && invApp.invoiceId != null)
          .map(invApp => ({
            creditNoteId: creditNoteApp.creditNoteId,
            creditNote: creditNoteApp.creditNote,
            invoiceId: invApp.invoiceId,
            invoice: invApp.invoice,
            amount: invApp.amount,
          })));

      const response = await typedApiFetch(`${REACT_APP_API_ROOT_URI}/credit_notes/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(allApplications.map((application) => ({
          creditNoteId: application.creditNoteId,
          invoiceId: application.invoiceId,
          amount: application.amount,
          paymentDate: makeYmd(applicationDate),
        }))),
      });

      if (!response.ok) {
        const errorData = await response.json() as { message?: string };
        throw new Error(errorData.message ?? 'Failed to apply credit note');
      }

      await response.json();

      setSuccess(`Successfully applied ${allApplications.length} credit note(s) to invoices.`);

      setTimeout(() => {
        onClose();
        onSuccess?.();
      }, FORM_RESET_DELAY);

    } catch (err) {
      console.error('Error applying credit notes:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  }, [creditNoteApplications, applicationDate, getAvailableCreditAmount, getSubmitValidationError, onClose, onSuccess]);

  // Helper function to check if there are any invoice applications
  const hasAnyInvoiceApplications = useCallback(() =>
    creditNoteApplications.some(app => app.invoiceApplications.length > 0),
  [creditNoteApplications]);

  /**
   * Calculates the total available credit across all credit note applications.
   *
   * Each intermediate accumulator value is passed through `floatVal` to
   * normalise floating-point representation after each addition, preventing
   * precision drift that can accumulate over multiple summations of cent-level
   * amounts (e.g. 0.1 + 0.2 !== 0.3 in IEEE 754 arithmetic).
   *
   * @returns The sum of available credit for every credit note in the current
   *          application list, as a normalised float.
   */
  const getTotalAvailableCredit = useCallback(() =>
    creditNoteApplications.reduce((total, creditNoteApp) =>
      floatVal(total + getAvailableCreditAmount(creditNoteApp.creditNote)),
    0),
  [creditNoteApplications, getAvailableCreditAmount]);

  // Calculate total amount being applied across all invoices
  const getTotalApplyingAmount = useCallback(() =>
    creditNoteApplications.reduce((total, creditNoteApp) =>
      total + creditNoteApp.invoiceApplications.reduce(
        (subtotal, invApp) => subtotal + (invApp.amount ?? 0),
        0,
      ),
    0),
  [creditNoteApplications]);

  // Calculate remaining available credit
  const getTotalRemainingCredit = useCallback(() =>
    getTotalAvailableCredit() - getTotalApplyingAmount(),
  [getTotalAvailableCredit, getTotalApplyingAmount]);

  return {
    // State
    creditNoteApplications,
    applicationDate,
    setApplicationDate,
    isSubmitting,
    error,
    success,
    invoiceSelectionOpen,
    selectedCreditNoteForInvoice,

    // Functions
    getAvailableCreditAmount,
    updateInvoiceApplicationAmount,
    addInvoiceApplication,
    removeInvoiceApplication,
    toggleCreditNoteExpansion,
    openInvoiceSelection,
    closeInvoiceSelection,
    handleInvoicesSelected,
    handleSubmit,
    removeAllInvoices,
    addAllSourceInvoices,
    hasAnyInvoiceApplications,
    getTotalAvailableCredit,
    getTotalApplyingAmount,
    getTotalRemainingCredit,
    getSubmitValidationError,
  };
};
